import * as T from 'three';

// Screen-space dust shares the head's WebGL renderer. No DOM overlays,
// additional canvas, textures, render targets, or pointer listeners.
const dustVertex = /* glsl */`
    attribute vec4 aDust;
    uniform float uPhase;
    uniform float uAspect;
    uniform float uDpr;
    varying float vDepth;
    varying float vLight;

    void main() {
        float depth = aDust.x;
        float phase = aDust.z;
        // Integer harmonics keep both position and velocity continuous
        // across the 120-second loop, without wrapping visible particles.
        vec2 drift = vec2(
            cos(uPhase + phase) + 0.20 * sin(uPhase * 2.0 + phase),
            sin(uPhase + phase) * 0.52 + 0.12 * cos(uPhase * 2.0 + phase)
        ) * mix(0.045, 0.18, depth);
        vec2 p = position.xy + vec2(drift.x / uAspect, drift.y);
        vec2 center = vec2(p.x * uAspect, p.y);
        float quietCenter = 1.0 - 0.78 * exp(-dot(center, center) * 7.0);
        float shimmer = 0.82 + 0.18 * pow(max(0.0, sin(uPhase * 2.0 + phase)), 4.0);
        vDepth = depth;
        vLight = aDust.w * shimmer * quietCenter;
        gl_PointSize = max(1.3 * uDpr, aDust.y * uDpr);
        gl_Position = vec4(p, 0.999, 1.0);
    }
`;

const dustFragment = /* glsl */`
    precision highp float;
    uniform vec3 uBlue;
    uniform vec3 uHighlight;
    varying float vDepth;
    varying float vLight;

    void main() {
        vec2 p = gl_PointCoord * 2.0 - 1.0;
        float radius2 = dot(p, p);
        if (radius2 > 1.0) discard;
        // A filled soft mote, never a ring or a star-shaped flare.
        float mote = exp(-radius2 * mix(2.4, 4.2, vDepth));
        float edge = 1.0 - smoothstep(0.68, 1.0, radius2);
        vec3 color = mix(uBlue, uHighlight, 0.32 + vDepth * 0.50);
        gl_FragColor = vec4(color * mix(0.78, 1.08, vDepth), mote * edge * vLight);
        #include <colorspace_fragment>
        // Keep the dust subordinate to the face, but visible on a phone.
        gl_FragColor.rgb *= 0.9;
    }
`;

export class CosmicDustBackground {
    readonly period = 120;
    readonly capacity = 9600;
    readonly points: T.Points;
    private geometry = new T.BufferGeometry();
    private material: T.ShaderMaterial;
    private previous: number | null = null;
    private elapsed = 0;
    private disposed = false;

    constructor(private scene: T.Scene, blue: T.Color, highlight: T.Color = blue) {
        // Reproducible layout; resizing never regenerates or jumps the dust.
        let seed = 831029;
        const random = () => {
            seed = (Math.imul(seed, 1664525) + 1013904223) >>> 0;
            return seed / 4294967296;
        };
        const positions = new Float32Array(this.capacity * 3);
        const dust = new Float32Array(this.capacity * 4);
        for (let i = 0; i < this.capacity; i++) {
            const x = (random() * 2 - 1) * 1.24;
            // Mix open space with a loose diagonal trail of discrete motes.
            // Overscan keeps the drift away from the viewport edges.
            const y = random() < 0.32
                ? Math.sin(x * 1.8) * 0.52 + (random() - 0.5) * 1.40
                : (random() * 2 - 1) * 1.24;
            const tier = random();
            const depth = tier < 0.72 ? 0.12 + random() * 0.26
                : tier < 0.96 ? 0.42 + random() * 0.28 : 0.76 + random() * 0.24;
            positions.set([x, y, 0], i * 3);
            dust.set([
                depth,
                depth < 0.4 ? 1.3 + random() * 0.7
                    : depth < 0.75 ? 2.0 + random() * 1.6 : 4.0 + random() * 3.0,
                random() * Math.PI * 2,
                0.44 + random() ** 1.3 * 0.46
            ], i * 4);
        }
        this.geometry.setAttribute('position', new T.BufferAttribute(positions, 3));
        this.geometry.setAttribute('aDust', new T.BufferAttribute(dust, 4));
        this.material = new T.ShaderMaterial({
            vertexShader: dustVertex,
            fragmentShader: dustFragment,
            uniforms: {
                uPhase: { value: 0 }, uAspect: { value: 1 },
                uDpr: { value: 1 }, uBlue: { value: blue.clone() },
                uHighlight: { value: highlight.clone() }
            },
            transparent: true,
            blending: T.AdditiveBlending,
            depthTest: false,
            depthWrite: false,
            toneMapped: false
        });
        this.points = new T.Points(this.geometry, this.material);
        this.points.name = 'MobileCosmicDustBackground';
        this.points.frustumCulled = false;
        this.points.renderOrder = -100;
        scene.add(this.points);
    }

    resize(width: number, height: number) {
        if (this.disposed) return;
        const w = Math.max(1, width), h = Math.max(1, height);
        this.material.uniforms.uAspect.value = w / h;
        this.geometry.setDrawRange(0, Math.min(this.capacity, Math.max(2200, Math.round(w * h / 115))));
    }

    update(renderer: T.WebGLRenderer, now: number, reduced = false) {
        if (this.disposed || document.hidden) return;
        if (this.previous !== null && !reduced) {
            this.elapsed += Math.min(0.1, Math.max(0, (now - this.previous) / 1000));
        }
        this.previous = now;
        this.material.uniforms.uPhase.value = (this.elapsed % this.period) / this.period * Math.PI * 2;
        this.material.uniforms.uDpr.value = renderer.getPixelRatio();
    }

    pause() { this.previous = null; }

    dispose() {
        if (this.disposed) return;
        this.disposed = true;
        this.scene.remove(this.points);
        this.geometry.dispose();
        this.material.dispose();
    }
}
